/* ═══════════════════════════════════════════════════════════════
   The Designer — the unified workspace.

   ONE surface, no modes. The body is always a text editor; the header,
   footer, pre-body and post-body bands are always zone-interactive.
   Everything else is a palette: Sections, Navigation, Levels, Layout
   Options, Header & Footer, Tokens, Project Tokens.

   Josh's law (docs/DESIGNER_UNIFIED_PLAN.md §1): the MENU is the
   permanent, complete access path — docking is convenience. So every
   palette body is rendered by `renderPalette` and hosted EITHER in a
   dock panel OR in a portalled floating panel, never in two places at
   once and never only in one.

   The dockview host is the SAME FormaDock the classic forma view uses
   (layout persistence, cloud sync, palette tray, lock model — reused,
   not forked), under its own designer-scoped key.

   Palette edits write through useFormaDraft: project copies save in
   place; masters fork the project's copy first (copy-on-write,
   existing model), silently.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import { getPanelData } from 'dockview';
import type { Editor } from '@tiptap/react';
import {
  Braces,
  LayoutTemplate,
  ListOrdered,
  ListTree,
  Map as MapIcon,
  PanelTop,
  Tags,
} from 'lucide-react';
import type { EmbeddedImage } from '../../shared/types';
import { FormaDock, type FormaDockHandle, type PaletteDef } from '../components/FormaDock';
import {
  CanvasSelectionContext,
  TokenClipboardContext,
  type CanvasSelectValue,
  type TokenClipboardOps,
} from '../components/TokenComponents';
import {
  setPlaceholderMemories,
  setPlaceholderProjectMode,
  setPlaceholderValues,
  setProjectTokenContents,
  setProjectTokenLabels,
} from '../utils';
import { resolveProjectTokenContent } from '../components/ProjectFormaTokens';
import { resolvePlaceholders, type PlaceholderContext } from '../../shared/forma-placeholders';
import { FormaService } from '../services/formaService';
import { reportError } from '../services/errorLog';
import type { DesignerDoc, ManualSection } from './useDesignerDoc';
import type { FormaDraft } from './useFormaDraft';
import { ManualRail } from './ManualRail';
import { NavigationPalette, type NavItem } from './Navigation';
import { ToolPanel } from './ToolPanel';
import { PanelDropdown } from './PanelDropdown';
import {
  type BandKind,
  type PageVariant,
  type StyleSelection,
  type ZoneColumn,
  zoneAppend,
  zoneApplyToKeys,
  zoneDeleteKeys,
  zoneInsertAt,
  zoneKeyFor,
  zoneMoveTokens,
  zoneRemoveAt,
  readZone,
  parseTokenKey,
} from './formaZonesModel';
import {
  LayoutPalette,
  LevelPalette,
  ProjectTokensPalette,
  TextTokenDialog,
  TokensPalette,
  ZonePalette,
  type TextDialogRequest,
} from './DesignPalettes';

/** The payload a token drag carries (PaletteChip / ZoneChip / multi-drag). */
export interface ZoneDropData {
  type?: string;
  value?: string;
  sourceZone?: string | null;
  sourceIndex?: number | null;
  keys?: string[];
  copy?: boolean;
}

export interface DesignWiring {
  pageVariant: PageVariant;
  styleSel: StyleSelection;
  onStyleSelect: (sel: StyleSelection) => void;
  /** A chip is airborne — the paper's bands light their drop targets. */
  dragActive: boolean;
  onZoneDrop: (band: BandKind, col: ZoneColumn, data: ZoneDropData) => void;
  /** The level the Levels palette is pointed at — its paragraphs light up. */
  levelFocus: number | null;
  /** Opens the shared literal-text editor for an on-sheet token or an empty
   *  band's quick-add (Studio only, Order 1 2026-08-13). Additive — absent
   *  in the Designer, which keeps its zone panel for text edits. */
  onRequestTextEdit?: (req: TextDialogRequest) => void;
  /** Click an empty band's column on the paper → add its first line of text
   *  right there, no panel required (Studio only). Additive — absent in the
   *  Designer, whose ZoneShell empty-hint keeps selecting only. */
  onZoneQuickAdd?: (band: BandKind, col: ZoneColumn) => void;
  /** Drop a token at an EXACT position in a zone (before/after another token,
   *  optionally on a new line) — the classic canvas's insertAtInZone. */
  onZoneInsertAt?: (
    zoneKey: string,
    idx: number,
    value: string,
    srcZone: string | null,
    srcIdx: number | null,
    newLine: boolean
  ) => void;
  /** Drop a token ONTO another to replace it — the classic replaceAtInZone. */
  onZoneReplaceAt?: (
    zoneKey: string,
    idx: number,
    value: string,
    srcZone: string | null,
    srcIdx: number | null
  ) => void;
  /** Open a tool panel by id from the paper — restores the classic canvas's
   *  click-the-end-of-section-line-to-edit-it shortcut. */
  onOpenPanel?: (id: string) => void;
}

/* ─── The palettes of the unified workspace ───
   Left/right docks are SUPERIOR (DESIGN.md §4). Default arrangement:
   Sections + Navigation left (tabbed), the Document in the middle taking
   every remaining pixel, zones + tokens right. Levels ships CLOSED — by
   the time a spec is in a project its levels are already defined, so level
   styling is a sub-tool you reach for, never the thing in your way. */
export const DESIGNER_PALETTES: PaletteDef[] = [
  { id: 'canvas', title: 'Document', closable: false, noTab: true },
  {
    id: 'sections',
    title: 'Sections',
    width: 260,
    defaultPosition: { direction: 'left', reference: 'canvas' },
  },
  {
    id: 'navigation',
    title: 'Navigation',
    width: 260,
    defaultPosition: { direction: 'within', reference: 'sections' },
  },
  {
    id: 'zone',
    title: 'Header & Footer',
    width: 340,
    defaultPosition: { direction: 'right', reference: 'canvas' },
  },
  {
    id: 'ds-layout',
    title: 'Layout Options',
    width: 340,
    defaultPosition: { direction: 'within', reference: 'zone' },
  },
  // Below the zone group — ONE right column, two stacked tab groups; the
  // Document keeps every remaining pixel (canvas is the hero).
  {
    id: 'tokens',
    title: 'Tokens',
    width: 340,
    defaultPosition: { direction: 'below', reference: 'zone' },
  },
  {
    id: 'project-tokens',
    title: 'Project Tokens',
    width: 340,
    defaultPosition: { direction: 'within', reference: 'tokens' },
  },
  {
    id: 'level-props',
    title: 'Levels',
    width: 340,
    defaultClosed: true,
    defaultPosition: { direction: 'within', reference: 'zone' },
  },
];

/** The glyph on each panel's top-bar button. A panel the user can dock,
    float or drop down needs to be recognisable at a glance in all three
    places — so the icon is defined once, here, next to the panel list. */
export const PANEL_ICONS: Record<string, React.ReactNode> = {
  sections: <ListTree size={15} aria-hidden="true" />,
  navigation: <MapIcon size={15} aria-hidden="true" />,
  zone: <PanelTop size={15} aria-hidden="true" />,
  'ds-layout': <LayoutTemplate size={15} aria-hidden="true" />,
  tokens: <Braces size={15} aria-hidden="true" />,
  'project-tokens': <Tags size={15} aria-hidden="true" />,
  'level-props': <ListOrdered size={15} aria-hidden="true" />,
};

/**
 * Is this screen point inside the dock — i.e. would letting go here DOCK the
 * panel rather than float it?
 *
 * §2.8's rule is spatial ("release outside every dock zone to leave it as a
 * floating box"), so the answer is geometry, not a guess about whether some
 * drop handler fired. The dockview body IS the dock zone; the header, the
 * toolbar and everything beyond the workspace are not.
 */
export function isOverDockZone(x: number, y: number): boolean {
  const el = document.querySelector('.dsg-workspace .forma-dock-body');
  const r = el?.getBoundingClientRect();
  if (!r) return false;
  return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
}

/** A panel the user opened without docking it. */
export interface FloatingTool {
  id: string;
  /** Where a drag-to-float released it, when that is how it got here. */
  at?: { left: number; top: number } | null;
}

/** One clipboard for token chips (module scope, like the classic canvas's —
    survives palette re-renders, dies with the window). */
let designTokenClipboard: string[] = [];

export interface WorkspaceProps {
  isDark: boolean;
  userId: string;
  locked: boolean;
  doc: DesignerDoc;
  draft: FormaDraft;
  canEdit: boolean;

  // ── Sections palette (the Manual rail) ──
  sections: ManualSection[];
  activeSectionId: string;
  onOpenSection: (specId: string) => void;
  onManageSections?: () => void;

  // ── Navigation palette ──
  editor: Editor | null;
  onJump: (item: NavItem) => void;

  // ── Zone / level tools ──
  styleSel: StyleSelection;
  onStyleSelect: (sel: StyleSelection) => void;
  /** Non-null when the user picked a level deliberately — the paper lights
      every paragraph of it so "what am I restyling" is never a guess. With
      nothing picked the Levels palette simply follows the caret. */
  levelFocus: number | null;
  onLevelFocus: (level: number) => void;
  pageVariant: PageVariant;
  onPageVariant: (v: PageVariant) => void;
  onDragState: (on: boolean) => void;
  dragActive: boolean;
  toast: (msg: string, isError?: boolean) => void;

  // ── Panel hosting (§2.8): dropped down, floating, or docked ──
  /** Panels the user opened without docking them. */
  floatingTools: FloatingTool[];
  onCloseFloating: (id: string) => void;
  onDockFloating: (id: string) => void;
  /** The panel currently dropped down from its top-bar button, if any. */
  dropdown: { id: string; anchor: { left: number; bottom: number } } | null;
  onCloseDropdown: () => void;
  /** Turn the dropdown into a floating panel where it stands. */
  onFloatDropdown: (id: string, at: { left: number; top: number }) => void;
  /** A DOCKED panel was dragged out of every dock zone and released. */
  onUndock: (id: string, at: { left: number; top: number }) => void;
  /** Which palettes the dock currently holds — drives the button states. */
  onDockPanelsChange: (ids: string[]) => void;
  onDockReady: (handle: FormaDockHandle) => void;
  /** A panel BUTTON is being dragged (the workspace shows the same
   *  float-preview feedback a docked panel gets). */
  panelDragId: string | null;
  /** firm_prefs key for this workspace's firm-default layout (§2.9). */
  firmLayoutKey?: string;

  /** Renders the document canvas, given the design wiring. */
  canvas: (wiring: DesignWiring) => React.ReactNode;
}

export function DesignerWorkspace({
  isDark,
  userId,
  locked,
  doc,
  draft,
  canEdit,
  sections,
  activeSectionId,
  onOpenSection,
  onManageSections,
  editor,
  onJump,
  styleSel,
  onStyleSelect,
  levelFocus,
  onLevelFocus,
  pageVariant,
  onPageVariant,
  onDragState,
  dragActive,
  toast,
  floatingTools,
  onCloseFloating,
  onDockFloating,
  dropdown,
  onCloseDropdown,
  onFloatDropdown,
  onUndock,
  onDockPanelsChange,
  onDockReady,
  panelDragId,
  firmLayoutKey,
  canvas,
}: WorkspaceProps) {
  const [dockEls, setDockEls] = React.useState<Record<string, HTMLElement>>({});
  const dockHandleRef = React.useRef<FormaDockHandle | null>(null);

  const forma = draft.forma;

  // The dock's open set, reported upward so the Tools menu can say where a
  // palette lives and decide whether to focus it or float it.
  React.useEffect(() => {
    onDockPanelsChange(Object.keys(dockEls));
  }, [dockEls, onDockPanelsChange]);

  // ─── Chip selection (ONE selection + ONE menu, same law as the classic canvas) ───
  const [selKey, setSelKey] = React.useState<string | null>(null);
  const [menuKey, setMenuKey] = React.useState<string | null>(null);
  const [selectedTokens, setSelectedTokens] = React.useState<string[]>([]);

  // Switching page variants MUST drop the selection (classic-canvas law: a
  // leftover selection would edit the page you are NOT looking at).
  React.useEffect(() => {
    setSelKey(null);
    setMenuKey(null);
    setSelectedTokens([]);
  }, [pageVariant]);

  const select = React.useCallback<CanvasSelectValue['select']>((key, opts) => {
    if (!key) {
      setSelKey(null);
      setMenuKey(null);
      setSelectedTokens([]);
      return;
    }
    setSelKey(key);
    if (key.startsWith('token:')) {
      setSelectedTokens((prev) =>
        opts?.keepSelection
          ? prev.includes(key)
            ? prev
            : [...prev, key]
          : opts?.additive
            ? prev.includes(key)
              ? prev.filter((k) => k !== key)
              : [...prev, key]
            : [key]
      );
    } else {
      setSelectedTokens([]);
    }
    setMenuKey(opts?.menu ? key : null);
  }, []);

  const applyToSelection = React.useCallback(
    (fn: (value: string) => string) => {
      if (!selectedTokens.length) return;
      draft.edit((f) => zoneApplyToKeys(f, selectedTokens, fn));
    },
    [draft, selectedTokens]
  );

  const moveTokens = React.useCallback(
    (targetZoneKey: string, keys: string[], copy: boolean) => {
      draft.edit((f) => zoneMoveTokens(f, targetZoneKey, keys, copy));
      setSelKey(null);
      setMenuKey(null);
      setSelectedTokens([]);
    },
    [draft]
  );

  // The Designer's paper has no marquee (its canvas is a text editor — a
  // drag there is a text selection). Selecting a set at once is still part
  // of the contract, so the Designer implements it too: the zone panel's
  // chips can be range-selected without inventing a second selection model.
  const selectMany = React.useCallback<CanvasSelectValue['selectMany']>((keys, additive) => {
    const tokens = keys.filter((k) => k.startsWith('token:'));
    setMenuKey(null);
    setSelectedTokens((prev) => (additive ? [...new Set([...prev, ...tokens])] : tokens));
    setSelKey(tokens.length ? tokens[tokens.length - 1] : null);
  }, []);

  const canvasSelValue = React.useMemo<CanvasSelectValue>(
    () => ({ selKey, menuKey, selectedTokens, select, applyToSelection, moveTokens, selectMany }),
    [selKey, menuKey, selectedTokens, select, applyToSelection, moveTokens, selectMany]
  );

  // ─── Clipboard triad (copy / cut / paste — DESIGN.md §4 ABSOLUTE) ───
  const clipOps = React.useMemo<TokenClipboardOps>(() => {
    const valueAt = (k: string) => {
      if (!forma) return undefined;
      const { zoneKey, idx } = parseTokenKey(k);
      return readZone(forma, zoneKey)[idx];
    };
    return {
      copyKeys: (keys) => {
        const values = keys
          .filter((k) => k.startsWith('token:'))
          .map(valueAt)
          .filter((v): v is string => v != null);
        if (values.length) designTokenClipboard = values;
      },
      cutKeys: (keys) => {
        const tokenKeys = keys.filter((k) => k.startsWith('token:'));
        const values = tokenKeys.map(valueAt).filter((v): v is string => v != null);
        if (values.length) designTokenClipboard = values;
        draft.edit((f) => zoneDeleteKeys(f, tokenKeys));
        select(null);
      },
      copySlot: (zoneKey) => {
        if (forma) designTokenClipboard = [...readZone(forma, zoneKey)];
      },
      cutSlot: (zoneKey) => {
        if (!forma) return;
        const arr = readZone(forma, zoneKey);
        designTokenClipboard = [...arr];
        draft.edit((f) =>
          zoneDeleteKeys(
            f,
            arr.map((_v, i) => `token:${zoneKey}:${i}`)
          )
        );
      },
      pasteAt: (targetKey) => {
        if (!designTokenClipboard.length) return;
        const values = [...designTokenClipboard];
        if (targetKey.startsWith('token:')) {
          const { zoneKey, idx } = parseTokenKey(targetKey);
          draft.edit((f) => {
            let next = f;
            values.forEach((v, i) => {
              next = zoneInsertAt(next, zoneKey, idx + 1 + i, v);
            });
            return next;
          });
        } else if (targetKey.startsWith('slot:')) {
          const zoneKey = targetKey.slice('slot:'.length);
          draft.edit((f) => {
            let next = f;
            for (const v of values) next = zoneAppend(next, zoneKey, v);
            return next;
          });
        }
      },
      canPaste: () => designTokenClipboard.length > 0,
    };
  }, [draft, forma, select]);

  // Keyboard: the triad + Delete on the CHIP selection, Esc clears it.
  // The document owns these keys whenever the caret is in it — a chip
  // selection is what makes them mean "the chip" instead.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tgt = e.target as HTMLElement | null;
      if (tgt && (/^(input|textarea|select)$/i.test(tgt.tagName) || tgt.isContentEditable)) return;
      const mod = e.ctrlKey || e.metaKey;
      const tokenSel = selectedTokens.filter((k) => k.startsWith('token:'));
      const slotZone = selKey?.startsWith('slot:') ? selKey.slice('slot:'.length) : null;
      if (mod && (e.key === 'c' || e.key === 'C') && (tokenSel.length || slotZone)) {
        e.preventDefault();
        if (tokenSel.length) clipOps.copyKeys(tokenSel);
        else if (slotZone) clipOps.copySlot(slotZone);
      } else if (mod && (e.key === 'x' || e.key === 'X') && (tokenSel.length || slotZone)) {
        e.preventDefault();
        if (tokenSel.length) clipOps.cutKeys(tokenSel);
        else if (slotZone) clipOps.cutSlot(slotZone);
      } else if (mod && (e.key === 'v' || e.key === 'V') && (selKey || slotZone)) {
        if (selKey) {
          e.preventDefault();
          clipOps.pasteAt(selKey);
        }
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && tokenSel.length) {
        e.preventDefault();
        draft.edit((f) => zoneDeleteKeys(f, tokenSel));
        select(null);
      } else if (e.key === 'Escape' && (selKey || menuKey || selectedTokens.length)) {
        select(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [clipOps, draft, menuKey, select, selKey, selectedTokens]);

  // ─── Custom tokens (firm-level, same store as the classic view) ───
  const [savedCustomTokens, setSavedCustomTokens] = React.useState<string[]>([]);
  const [recentCustomTokens, setRecentCustomTokens] = React.useState<string[]>([]);
  React.useEffect(() => {
    let live = true;
    FormaService.loadTokens()
      .then((t) => {
        if (live) setSavedCustomTokens(t);
      })
      .catch((err) => reportError('Designer:loadTokens', err));
    return () => {
      live = false;
    };
  }, []);
  const persistSaved = (next: string[]) => {
    setSavedCustomTokens(next);
    FormaService.saveTokens(next).catch((err) => reportError('Designer:saveTokens', err));
  };
  const noteCustomToken = React.useCallback((t: string) => {
    setRecentCustomTokens((p) => [t, ...p.filter((x) => x !== t)].slice(0, 10));
  }, []);

  // ─── Text dialog + image picker (shared by palette and paper drops) ───
  const [textDialog, setTextDialog] = React.useState<TextDialogRequest | null>(null);
  const requestImage = React.useCallback(async (): Promise<string | null> => {
    if (!window.api?.openImageDialog) {
      toast('Adding images needs the desktop app', true);
      return null;
    }
    const imgData = (await window.api.openImageDialog()) as EmbeddedImage | null;
    if (!imgData) return null;
    const imgId = 'img_' + Date.now().toString(36);
    draft.edit((f) => ({ ...f, images: { ...(f.images || {}), [imgId]: imgData } }));
    return `{Image:${imgId}}`;
  }, [draft, toast]);

  // ─── Project context for placeholders + project tokens ───
  const placeholderCtx = React.useMemo<PlaceholderContext>(
    () => ({
      project: doc.projectMeta,
      issuances: doc.projectIssuances,
      dateFormat: forma?.docSettings?.dateFormat,
    }),
    [doc.projectMeta, doc.projectIssuances, forma?.docSettings?.dateFormat]
  );

  const projectTokens = React.useMemo(() => {
    const proj = doc.project as {
      formaTokens?: Array<{ id: string; name: string; content?: string }>;
    } | null;
    if (!proj?.formaTokens) return [] as Array<{ id: string; name: string; content: string }>;
    return proj.formaTokens.map((t) => ({
      id: t.id,
      name: t.name,
      content: resolveProjectTokenContent(t.content || '', doc.projectMeta),
    }));
  }, [doc.project, doc.projectMeta]);

  // Published during RENDER (not an effect): the zone chips read these
  // registries while they render — same rule the classic view documents.
  setPlaceholderProjectMode(!!forma?.projectId);
  setPlaceholderMemories(
    Object.fromEntries(Object.entries(forma?.placeholders || {}).map(([id, p]) => [id, p.was]))
  );
  setPlaceholderValues(forma ? resolvePlaceholders(forma, placeholderCtx) : {});
  setProjectTokenLabels(Object.fromEntries(projectTokens.map((t) => [t.id, t.name])));
  setProjectTokenContents(Object.fromEntries(projectTokens.map((t) => [t.id, t.content])));

  // ─── Drops onto the paper's bands ───
  const onZoneDrop = React.useCallback(
    (band: BandKind, col: ZoneColumn, d: ZoneDropData) => {
      const zoneKey = zoneKeyFor(band, pageVariant, col);
      onStyleSelect({ kind: 'zone', band });
      if (d.type === 'tokenMulti' && d.keys?.length) {
        moveTokens(zoneKey, d.keys, !!d.copy);
        return;
      }
      const value = d.value;
      if (value == null) return;
      if (d.type === 'token' && d.copy && d.sourceZone != null && d.sourceIndex != null) {
        draft.edit((f) =>
          zoneMoveTokens(f, zoneKey, [`token:${d.sourceZone}:${d.sourceIndex}`], true)
        );
        return;
      }
      if (value === '__custom__') {
        const srcZ = d.sourceZone ?? null;
        const srcI = d.sourceIndex ?? null;
        setTextDialog({
          title: 'Custom text',
          initial: '',
          onCommit: (text) => {
            if (!text) return;
            draft.edit((f) => {
              let next = f;
              if (srcZ != null && srcI != null) next = zoneRemoveAt(next, srcZ, srcI);
              return zoneAppend(next, zoneKey, text);
            });
            noteCustomToken(text);
          },
        });
        return;
      }
      if (value === '{Image}' && d.sourceZone == null) {
        void requestImage().then((tok) => {
          if (tok) draft.edit((f) => zoneAppend(f, zoneKey, tok));
        });
        return;
      }
      draft.edit((f) => {
        let next = f;
        if (d.sourceZone != null && d.sourceIndex != null) {
          next = zoneRemoveAt(next, d.sourceZone, d.sourceIndex);
        }
        return zoneAppend(next, zoneKey, value);
      });
    },
    [draft, moveTokens, noteCustomToken, onStyleSelect, pageVariant, requestImage]
  );

  // Click-to-insert from the token rails: lands in the selected slot, else the
  // selected band (a column that already has content, else Center).
  const insertFromPalette = React.useCallback(
    (value: string) => {
      let zoneKey: string | null = null;
      if (selKey?.startsWith('slot:')) zoneKey = selKey.slice('slot:'.length);
      else if (styleSel?.kind === 'zone' && forma) {
        const band = styleSel.band;
        const withContent = (['C', 'L', 'R'] as ZoneColumn[]).find(
          (c) => readZone(forma, zoneKeyFor(band, pageVariant, c)).length > 0
        );
        zoneKey = zoneKeyFor(band, pageVariant, withContent ?? 'C');
      }
      if (!zoneKey) {
        toast('Click a header or footer band first, then click a chip to add it');
        return;
      }
      if (value === '{Image}') {
        const zk = zoneKey;
        void requestImage().then((tok) => {
          if (tok) draft.edit((f) => zoneAppend(f, zk, tok));
        });
        return;
      }
      const zk = zoneKey;
      draft.edit((f) => zoneAppend(f, zk, value));
    },
    [draft, forma, pageVariant, requestImage, selKey, styleSel, toast]
  );

  const wiring: DesignWiring = {
    pageVariant,
    styleSel,
    onStyleSelect,
    dragActive,
    onZoneDrop,
    levelFocus,
  };

  /* ─── ONE renderer per palette, hosted by the dock OR by a floating
         panel. This is what makes "the whole feature set works menu-only"
         true rather than aspirational. ─── */
  const renderPalette = React.useCallback(
    (id: string): React.ReactNode => {
      switch (id) {
        case 'sections':
          return (
            <ManualRail
              sections={sections}
              activeId={activeSectionId}
              onOpen={onOpenSection}
              onManageSections={onManageSections}
            />
          );
        case 'navigation':
          return <NavigationPalette editor={editor} forma={forma} onJump={onJump} />;
        case 'level-props':
          return forma ? (
            <LevelPalette
              forma={forma}
              editor={editor}
              levelFocus={levelFocus}
              onLevel={onLevelFocus}
              onEdit={draft.edit}
              disabled={!canEdit}
            />
          ) : null;
        case 'ds-layout':
          return forma ? (
            <LayoutPalette
              forma={forma}
              onEdit={draft.edit}
              pageVariant={pageVariant}
              onPageVariant={onPageVariant}
            />
          ) : null;
        case 'zone':
          return forma ? (
            <ZonePalette
              forma={forma}
              band={styleSel?.kind === 'zone' ? styleSel.band : null}
              variant={pageVariant}
              onEdit={draft.edit}
              images={forma.images || {}}
              onRequestText={setTextDialog}
              onRequestImage={requestImage}
              onCustomTokenUsed={noteCustomToken}
            />
          ) : null;
        case 'tokens':
          return (
            <TokensPalette
              savedTokens={savedCustomTokens}
              recentTokens={recentCustomTokens}
              onSaveToken={(t) => persistSaved([...savedCustomTokens.filter((x) => x !== t), t])}
              onRemoveSavedToken={(t) => persistSaved(savedCustomTokens.filter((x) => x !== t))}
              onDragState={onDragState}
              onInsert={insertFromPalette}
            />
          );
        case 'project-tokens':
          return forma ? (
            <ProjectTokensPalette
              forma={forma}
              ctx={placeholderCtx}
              projectTokens={projectTokens}
              issuanceNames={doc.projectIssuances.map((i) => i.name || '').filter(Boolean)}
              onEdit={draft.edit}
              onDragState={onDragState}
              onInsert={insertFromPalette}
            />
          ) : null;
        default:
          return null;
      }
    },
    // persistSaved is a plain closure over savedCustomTokens, which is
    // already a dependency.
    [
      sections,
      activeSectionId,
      onOpenSection,
      onManageSections,
      editor,
      forma,
      onJump,
      levelFocus,
      onLevelFocus,
      canEdit,
      draft.edit,
      pageVariant,
      onPageVariant,
      styleSel,
      requestImage,
      noteCustomToken,
      savedCustomTokens,
      recentCustomTokens,
      onDragState,
      insertFromPalette,
      placeholderCtx,
      projectTokens,
      doc.projectIssuances,
    ]
  );

  const titleOf = (id: string) => DESIGNER_PALETTES.find((p) => p.id === id)?.title || id;

  /* ─── DRAG-AWAY UNDOCK (§2.8) ───
     Drag a DOCKED panel off its edge and let go outside every dock zone and
     it becomes a floating box. The commitment happens on RELEASE, never on
     hover, and while the pointer is out there the dock's blue drop preview
     is replaced by a floating-card preview — so "letting go here will float
     it" is visible before you commit, not discovered afterwards.

     Panel BUTTON drags (§2.8's other half) get the identical preview, so the
     two gestures teach each other. */
  const [floatPreview, setFloatPreview] = React.useState<{
    x: number;
    y: number;
    title: string;
  } | null>(null);
  const pendingFloatRef = React.useRef<{ id: string; at: { left: number; top: number } } | null>(
    null
  );
  const onUndockRef = React.useRef(onUndock);
  onUndockRef.current = onUndock;
  const panelDragIdRef = React.useRef(panelDragId);
  panelDragIdRef.current = panelDragId;
  // The listeners below bind once; they read the current title lookup through
  // a ref rather than re-binding on every render.
  const titleOfRef = React.useRef(titleOf);
  titleOfRef.current = titleOf;

  React.useEffect(() => {
    /** The id being dragged, and whether the dock owns it. */
    const dragging = (): { id: string; docked: boolean } | null => {
      const panelId = getPanelData()?.panelId;
      // The Document is the anchored core — it never floats (FormaDock
      // already refuses to close it).
      if (panelId && panelId !== 'canvas') return { id: panelId, docked: true };
      const btn = panelDragIdRef.current;
      return btn ? { id: btn, docked: false } : null;
    };

    const onDragOver = (e: DragEvent) => {
      const drag = dragging();
      if (!drag) return;
      if (isOverDockZone(e.clientX, e.clientY)) {
        pendingFloatRef.current = null;
        setFloatPreview(null);
        return;
      }
      // Out here, letting go floats it — say so, and accept the drop so the
      // cursor stops reading "not allowed".
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      // Only a DOCKED panel is completed here; a button drag is finished by
      // the button's own dragend (it knows whether the dock took it).
      pendingFloatRef.current = drag.docked
        ? { id: drag.id, at: { left: e.clientX, top: e.clientY } }
        : null;
      setFloatPreview({ x: e.clientX, y: e.clientY, title: titleOfRef.current(drag.id) });
    };

    const settle = () => {
      const pending = pendingFloatRef.current;
      pendingFloatRef.current = null;
      setFloatPreview(null);
      if (pending) onUndockRef.current(pending.id, pending.at);
    };

    document.addEventListener('dragover', onDragOver, true);
    document.addEventListener('drop', settle, true);
    document.addEventListener('dragend', settle, true);
    return () => {
      document.removeEventListener('dragover', onDragOver, true);
      document.removeEventListener('drop', settle, true);
      document.removeEventListener('dragend', settle, true);
    };
  }, []);

  return (
    <CanvasSelectionContext.Provider value={canvasSelValue}>
      <TokenClipboardContext.Provider value={clipOps}>
        <div className="dsg-workspace">
          <FormaDock
            isDark={isDark}
            locked={locked}
            palettes={DESIGNER_PALETTES}
            // Additive designer-scoped key. NEVER reuse sf.formaDock.v4 —
            // the classic editor's key, now historical (nothing writes it
            // any more; a firm may still carry old blobs under it, which
            // is harmless). v2: the workspace itself changed shape (the
            // Sections rail moved INTO the dock), so v1 no longer describes it.
            layoutKey={`sf.designerDock.v2.${userId || 'anon'}`}
            firmLayoutKey={firmLayoutKey}
            onContainersChange={setDockEls}
            onReady={(h) => {
              dockHandleRef.current = h;
              onDockReady(h);
            }}
          />

          {dockEls.canvas && createPortal(canvas(wiring), dockEls.canvas)}

          {DESIGNER_PALETTES.filter((p) => p.id !== 'canvas').map((p) =>
            dockEls[p.id] ? (
              <React.Fragment key={p.id}>
                {createPortal(renderPalette(p.id), dockEls[p.id])}
              </React.Fragment>
            ) : null
          )}

          {/* Floating panels. Same bodies, no dock required. */}
          {floatingTools
            .filter((f) => !dockEls[f.id])
            .map((f, i) => (
              <ToolPanel
                key={f.id}
                title={titleOf(f.id)}
                index={i}
                at={f.at}
                onDock={() => onDockFloating(f.id)}
                onClose={() => onCloseFloating(f.id)}
              >
                {renderPalette(f.id)}
              </ToolPanel>
            ))}

          {/* The panel dropped down from its top-bar button (§2.8). */}
          {dropdown && !dockEls[dropdown.id] && (
            <PanelDropdown
              title={titleOf(dropdown.id)}
              anchor={dropdown.anchor}
              onFloat={() =>
                onFloatDropdown(dropdown.id, {
                  left: dropdown.anchor.left + 180,
                  top: dropdown.anchor.bottom + 14,
                })
              }
              onClose={onCloseDropdown}
            >
              {renderPalette(dropdown.id)}
            </PanelDropdown>
          )}

          {/* "Let go here and it floats" — the preview that replaces the
              dock's drop overlay once the pointer leaves every dock zone. */}
          {floatPreview &&
            createPortal(
              <div
                className="dsg-float-preview"
                aria-hidden="true"
                style={{ left: floatPreview.x, top: floatPreview.y }}
              >
                <span className="dsg-float-preview-bar">{floatPreview.title}</span>
                <span className="dsg-float-preview-hint">Release to float</span>
              </div>,
              document.body
            )}

          {textDialog && (
            <TextTokenDialog request={textDialog} onClose={() => setTextDialog(null)} />
          )}
        </div>
      </TokenClipboardContext.Provider>
    </CanvasSelectionContext.Provider>
  );
}
